import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { BountyDto } from '@roadfootage/api-client'
import { useApi } from '../../composables/api'

export type BountySortOrder = '-createdAt' | '-rewardAmountPoints'

const PAGE_SIZE = 12
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const useBountiesStore = defineStore('bounties', () => {
  const { publicBountiesApi } = useApi()
  const bounties = ref<BountyDto[]>([])
  const sort = ref<BountySortOrder>('-createdAt')
  const isLoading = ref(false)
  const totalCount = ref(0)
  const hasMore = computed(() => bounties.value.length < totalCount.value)

  // Derived from item count so it can never drift out of sync across navigations.
  const nextPage = computed(() => Math.floor(bounties.value.length / PAGE_SIZE) + 1)

  async function fetchBounties() {
    if (isLoading.value) return
    isLoading.value = true
    try {
      const response = await publicBountiesApi.publicBountiesGetPublicBounties({
        page: nextPage.value,
        pageSize: PAGE_SIZE,
        sort: sort.value
      })
      bounties.value.push(...(response.items ?? []))
      totalCount.value = response.totalCount ?? totalCount.value
    } catch (error) {
      console.error('Failed to fetch bounties', error)
    } finally {
      isLoading.value = false
    }
  }

  // The bounty a notification pointed at (`/bounties?id=`). It may have closed since, so "missing" is a normal outcome.
  const focused = ref<BountyDto | null>(null)
  const focusedMissing = ref(false)
  let focusRequest = 0

  async function loadFocused(id: string | null | undefined) {
    const request = ++focusRequest
    focused.value = null
    focusedMissing.value = false
    if (!id) return
    if (!GUID.test(id)) {
      focusedMissing.value = true
      return
    }

    try {
      const response = await publicBountiesApi.publicBountiesGetPublicBounties({
        pageSize: 1,
        filters: JSON.stringify([{ k: 'id', v: id, op: 'eq' }])
      })
      if (request !== focusRequest) return
      const found = response.items?.find((b) => b.id === id) ?? null
      // A claimed bounty is closed for responses, so it counts as gone.
      focused.value = found && !found.isClaimed ? found : null
      focusedMissing.value = focused.value === null
    } catch (error) {
      // The list below still works; just show no banner.
      console.error('Failed to load the focused bounty', error)
    }
  }

  function reset() {
    bounties.value = []
    totalCount.value = 0
  }

  return {
    bounties,
    sort,
    isLoading,
    hasMore,
    focused,
    focusedMissing,
    fetchBounties,
    loadFocused,
    reset
  }
})
